/**
 * client/src/pages/Kept.tsx — what "Keep it" lands on. §2.4, §3.13's ordering.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THIS FILE EXISTS BECAUSE "Keep it" DROPPED HER INTO THE ENGINEERING CONSOLE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * The last thing the product offers her is `FAMILY.keep.offerTitle` / `offerBody` — *"Would you like
 * to keep this? Make an account and this voice is yours."* — and after signing up she arrived at
 * `/voices`, which read, in 11–12 px letter-spaced capitals:
 *
 *     amma@vox   READ   UNCLAIMED   CONSENT PENDING — ASR HAS NOT RUN   no rev
 *
 * and, one click further, `AMMA@VOX`, `owner usr_4dl7lxwo9o1ks6lrc550qz66t`,
 * `session ses_89dnyl98n1z86e1j07g230fps`, *"consent WER  not measured (max 0.35)"*, and *"Retuning,
 * previews and the bake-off spend spark-1 and ship with it."* **With no buttons on it at all** —
 * including any way to claim the thing labelled UNCLAIMED.
 *
 * Four of those words are forbidden on a family screen (§3.2's vocabulary rule, VOX-LAW 16) and the
 * raw WER printed against its threshold is precisely the "number on screen is an invitation to be
 * judged by it" that VOX-LAW 16 exists to prevent — shown to the person the number is about, at the
 * moment she has just been told the voice is hers.
 *
 * So: `/voices` and `/voices/:id` stay exactly as they are. They are **Karti's console**, they are
 * good at that, and §3.13 puts them behind sign-in on purpose — *"`Voices`, `Bakeoff` and `Admin` are
 * Karti's console and live behind sign-in."* What changes is that the family path no longer ends
 * there. This is where it ends.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * IT CLAIMS — BECAUSE SIGNING UP ONLY DOES HALF OF IT, AND THIS SCREEN ASSERTS THE WHOLE THING
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `claimGuest` in `server/src/auth.ts` runs inside signup and covers **the same-browser case**: it
 * moves `voice_sessions.actorKey` to the new user and sets `voices.ownerUserId` for every voice out
 * of those sessions. That is the common path — she records and then presses "Keep it" in the same tab
 * — and on it this screen has nothing to do.
 *
 * ⚠️ **It does not cover the claim-link case, and that is exactly the case §2.4 built the link for.**
 * A grandchild opening `/c/:token` weeks later, on a phone that has never seen this site, is a *new*
 * guest with no sessions of his own; `claimGuest` moves nothing, and this screen would have said
 * *"the voice is yours now"* over a voice that was still unowned. Verified in a cold browser: the
 * claim link, "Keep it", signup — and `voices.ownerUserId` was still null underneath the sentence.
 *
 * So the claim runs here, with the token this page is holding, through the route that exists for it:
 * `POST /api/v1/voices/:id/claim`, whose predicate (`canClaim`) is *"a signed-in user AND either a
 * live claim token for this voice's session or the same browser that recorded it"*. It is an effect
 * and not a button, because there is nothing to decide: pressing "Keep it" **was** the decision, and
 * a second "Claim this voice" control after signing up is the product asking twice.
 *
 * ⚠️ **The title is not rendered until the voice is actually owned.** Until then the screen shows the
 * link and the retention promise, which are true unconditionally. An ownership sentence over an
 * unowned voice is the same defect as *"Sent."* over nothing sent, on the screen she was sent to by a
 * button that said the voice would be hers.
 *
 * What this screen owes her otherwise is §3.13's ordering, finished: **say it is saved, say what
 * happens next, and give her the durable handle again.**
 *
 * ⚠️ **The claim link is repeated here even though she now has an account**, and that is VOX-LAW 20
 * rather than duplication: the account is one way back and the link is the one that works from a
 * browser that has never seen this site, with no password, on a phone somebody hands her. §2.4's
 * promise does not expire when she signs in.
 *
 * ⚠️ **Every string comes from `shared/voice/copy.ts`** and `test/no-raw-strings-in-studio.test.ts`
 * now walks this directory, so it cannot stop being true silently — which is the guard whose absence
 * let the console reach a signed-in family member in the first place.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { SessionLinks } from "../../../shared/protocol.ts";
import { FAMILY } from "../../../shared/voice/copy.ts";
import { api, claimView } from "../lib/api.ts";
import { emailClaimLink } from "../lib/claimLink.ts";
import { useSessionHandle } from "../lib/handle.ts";
import { say } from "../lib/say.ts";
import { useSession } from "../lib/session.tsx";
import { Blank, Button, CopyButton, Field, Notice, Stage } from "../ui/primitives.tsx";

export function Kept() {
  /** A `ses_` id or a `vox_` claim token — the same handle every re-entrant screen takes. */
  const { sessionId: handle = "" } = useParams();
  const navigate = useNavigate();
  const { app } = useSession();
  const { session, sessionId, claimToken, notFound } = useSessionHandle(handle, "kept");

  const [links, setLinks] = useState<SessionLinks | null>(null);
  const [owned, setOwned] = useState(false);
  const [email, setEmail] = useState("");
  const [emailed, setEmailed] = useState(false);
  const tokenRef = useRef<HTMLParagraphElement | null>(null);
  /** Once. Claiming is idempotent on the server, but two in-flight POSTs is two 409s to ignore. */
  const claimed = useRef(false);

  useEffect(() => {
    if (sessionId === null) return;
    const ac = new AbortController();
    api.links(sessionId, ac.signal, claimToken).then(setLinks, () => {
      // The link is a section of this page, not the page. Losing it is not a reason to withhold the
      // sentence that says her voice is safe.
    });
    return () => ac.abort();
  }, [sessionId, claimToken]);

  /**
   * Make the promise true, then say it. See the header for the case this covers and the case signup
   * already covered.
   *
   * `claimView` rather than `GET /voices`: the voice this session produced is not in the new user's
   * list yet (that is the whole problem), and `/c/:token` is the one route a claim token opens on its
   * own. It is also the only projection on this page that carries `ownerUserId`, which is how the
   * *same-browser* case — already claimed by `claimGuest` during signup — is **read** rather than
   * assumed. A voice that already has an owner is left alone.
   *
   * ⚠️ **Every route into this page passes a CLAIM TOKEN, not a session id**, precisely so this
   * effect always has something to resolve: `Studio.tsx` hands `session.claimToken` to `onSignIn`,
   * and `Reveal.tsx` prefers `links.claimToken` over its own path segment. A `ses_` id here would
   * leave ownership unknowable and the title unrenderable, which is the honest failure but a
   * needless one.
   */
  useEffect(() => {
    if (claimToken === null || claimed.current) return;
    claimed.current = true;
    const ac = new AbortController();
    void (async () => {
      try {
        const view = await claimView(claimToken, ac.signal);
        if (!view.voice) return;
        if (view.voice.ownerUserId !== null) {
          setOwned(true);
          return;
        }
        await api.claimVoice(view.voice.id, claimToken);
        setOwned(true);
      } catch (err) {
        // VOX-LAW 16b: nothing about this reaches her screen. The consequence is visible instead —
        // the ownership sentence is simply not rendered, and the link and the retention promise are.
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error(`kept: claim failed — ${(err as Error).message}`);
      }
    })();
    return () => ac.abort();
  }, [claimToken]);

  const helper = app?.helperName ?? null;
  const name = session?.subjectName ?? null;

  /**
   * ⚠️ `notFound` and a still-resolving session are different states and this page must not merge
   * them (`lib/handle.ts` says so in writing). A dead handle gets the designed sentence; a pending
   * one gets nothing yet, because accusing a link of being dead for as long as the network takes is
   * the failure that sentence exists to avoid.
   */
  if (notFound) {
    return (
      <Stage>
        <h1 className="vox-say">{FAMILY.back.title}</h1>
        <Notice>{helper ? say(FAMILY.back.notReady, { helper }) : FAMILY.mishap.keepGoing}</Notice>
        <p className="vox-quiet">{FAMILY.keep.retention}</p>
      </Stage>
    );
  }

  // Every sentence below fills `{name}` or `{helper}`, and `say()`'s fallback is a warm sentence
  // about a recording rather than a title. Nothing renders until both are known.
  if (!name || !helper) return <Blank />;

  return (
    <Stage top>
      {owned ? (
        <>
          <h1 className="vox-say">{say(FAMILY.keep.savedTitle, { name })}</h1>
          <p className="vox-lead">{FAMILY.keep.savedBody}</p>
          <p className="vox-quiet">{say(FAMILY.keep.savedNext, { helper })}</p>
        </>
      ) : (
        // Still resolving, or the claim did not land. The link below is true either way, and §2.4's
        // retention promise is what actually keeps the recording — not the account.
        <h1 className="vox-say">{FAMILY.back.title}</h1>
      )}

      {links && (
        <section className="vox-card" aria-labelledby="kept-link-title" style={{ marginTop: "var(--space-5)" }}>
          <h2 id="kept-link-title" className="vox-lead">
            {FAMILY.keep.linkTitle}
          </h2>
          <p className="vox-quiet" style={{ marginTop: "var(--space-2)" }}>
            {FAMILY.keep.linkBody}
          </p>

          <p className="vox-token" ref={tokenRef} style={{ marginTop: "var(--space-4)" }}>
            {links.claimUrl}
          </p>

          <div className="vox-copyrow" style={{ marginTop: "var(--space-4)" }}>
            <CopyButton
              value={links.claimUrl}
              label={FAMILY.keep.linkCopy}
              copiedLabel={FAMILY.keep.linkCopied}
              onFail={() => {
                // Clipboard refused. Select the text so the next thing she does works — the same
                // fallback the payoff screen uses, for the same reason.
                const node = tokenRef.current;
                if (!node) return;
                const range = document.createRange();
                range.selectNodeContents(node);
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(range);
              }}
            />
          </div>

          <div style={{ marginTop: "var(--space-5)" }}>
            <Field
              label={FAMILY.keep.linkEmailLabel}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <div className="vox-btn-row" style={{ marginTop: "var(--space-3)" }}>
              <Button
                onClick={() => {
                  // `emailClaimLink` opens her own mail client — nothing is sent, and
                  // `linkEmailSent` says exactly that. See `lib/claimLink.ts`.
                  if (emailClaimLink(email, links.claimUrl)) setEmailed(true);
                }}
              >
                {emailed ? FAMILY.keep.linkEmailSent : FAMILY.keep.linkEmailButton}
              </Button>
            </div>
          </div>

          <p className="vox-quiet" style={{ marginTop: "var(--space-5)" }}>
            {FAMILY.keep.retention}
          </p>
        </section>
      )}

      <div className="vox-btn-row" style={{ marginTop: "var(--space-5)" }}>
        {/*
          One way out, and it goes to the door rather than to `/voices`. She is signed in now, so the
          door offers her the console link — which is how the console stays reachable without ever
          being handed to her.
        */}
        <Button variant="primary" onClick={() => navigate("/", { replace: true })}>
          {FAMILY.keep.savedHome}
        </Button>
      </div>
    </Stage>
  );
}
